import { useCallback } from 'react';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Camera } from 'src/models/Camera.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import { AutoConnectCameraStatus } from 'src/models/CameraMode.model';
import { PracticeSettings } from 'src/models/Settings.model';

import { captureError } from 'src/utils/errorReporting';

import useUiTranslation from './useUiTranslation';

const useAutoConnectCamera = () => {
  const { cameraIP } = useLocalStore();
  const { setCameraData, setAutoConnectCameraStatus } = useRemoteBoundStore();

  const { tCommon } = useUiTranslation();

  const autoConnectCamera = useCallback(
    async (practiceSettingsData: PracticeSettings) => {
      if (practiceSettingsData.camera !== Camera.SMALL) return;

      if (!cameraIP) {
        setAutoConnectCameraStatus(AutoConnectCameraStatus.FAILED);
        return;
      }

      try {
        setAutoConnectCameraStatus(AutoConnectCameraStatus.CONNECTING);

        const cameraData = await CameraConnection.loadCameraData(
          cameraIP,
          tCommon('message.error.cameraIpMissingMsg'),
        );

        await CameraConnection.getInstance().say(
          'Camera successfully connected.',
        );
        setCameraData(cameraData);
        setAutoConnectCameraStatus(AutoConnectCameraStatus.CONNECTED);
      } catch (error) {
        setAutoConnectCameraStatus(AutoConnectCameraStatus.FAILED);

        captureError(
          error,
          'Failed to connect to the camera using the existing IP address',
        );
      }
    },
    [cameraIP, setAutoConnectCameraStatus, setCameraData, tCommon],
  );

  return { autoConnectCamera };
};

export default useAutoConnectCamera;
